import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	DestroyRef,
	ElementRef,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
	ViewChild,
} from '@angular/core';
import { PreorderType } from '../../../smoothr-web-app-core/enums/PreorderType';
import dayjs from 'dayjs';
import { TranslateService } from '@ngx-translate/core';
import { RepositoryService } from '../../../smoothr-web-app-core/services/repository/repository.service';
import { AlertController, ModalController } from '@ionic/angular';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import RepositoryInterface from '../../../smoothr-web-app-core/directives/repository-directive';
import { OrderListAction } from '../../enums/OrderListAction';
import { AppComponent } from '../../app.component';
import { ModalInfoComponent } from '../modal-info/modal-info.component';
import { getDeinstagCount, OrderUtils } from '../../../smoothr-web-app-core/utils/order-utils';
import Article from '../../../smoothr-web-app-core/models/Article';
import ArticleOption from '../../../smoothr-web-app-core/models/ArticleOption';
import Order from '../../../smoothr-web-app-core/models/Order';
import {
	axiosErrorToMessage,
	defaultsToArticleOption,
	getPrice,
	numberD,
	numberToCurrency,
	sleep,
} from '../../../smoothr-web-app-core/utils/utils';
import { NoteArticleModalComponent } from '../note-article-modal/note-article-modal.component';
import { NavigationService } from 'src/app/services/navigation.service';
import ArticleGroup from 'src/smoothr-web-app-core/models/ArticleGroup';
import { debounceTime, Subscription } from 'rxjs';
import PromoCode from 'src/smoothr-web-app-core/models/PromoCode';
import { Location } from '@angular/common';
import type { DiscountsModalResult } from '../discounts-modal/discounts-modal';
import { DiscountType } from '../../enums/DiscountType';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import ArticleCategory from '../../../smoothr-web-app-core/models/ArticleCategory';
import { ArticleCategoryType } from '../../../smoothr-web-app-core/enums/ArticleCategoryType';
import { Api } from '../../../smoothr-web-app-core/api/api';
import { environment } from '../../../environments/environment';
import { isDoubleMonday } from '../../shared';

@Component({
	selector: 'app-order-content',
	templateUrl: './order-content.component.html',
	styleUrls: ['order-content.component.scss'],
	standalone: false,
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderContentComponent extends RepositoryInterface implements OnInit, OnDestroy {
	dayjs = dayjs;
	isValid = false;
	loading = false;
	ou = OrderUtils;
	pt = PreorderType;
	differenceToMvo: number;
	@Input()
	fromMenu = false;
	getPrice = getPrice;
	promoError: string;
	tip = 0;
	totalPriceValue = '';
	totalPriceBeforeDiscount = '';
	recommendationArticles: Article[] = [];
	numberToCurrency = numberToCurrency;
	numberD = numberD;
	public amountOfPointsNotRegister = 0;
	promoDiscountValue = '';
	@Output() openCheckout = new EventEmitter<void>();
	@Output() closeOrderContent = new EventEmitter<void>();
	@ViewChild('swiperSlider') swiper: ElementRef | null;
	private subscriptions: Subscription = new Subscription();
	get doubleMondayAvailable(): boolean {
		return this.repository.doubleMondayEnabled();
	}
	promoCategories: ArticleCategory[] = this.repository._venue.articleCategories.filter(
		it => it.mainCategory === ArticleCategoryType.PROMOS
	);

	constructor(
		public translate: TranslateService,
		protected repository: RepositoryService,
		private modalCtrl: ModalController,
		private snackbarCtrl: MatSnackBar,
		private router: Router,
		private route: ActivatedRoute,
		private alertCtrl: AlertController,
		private navigationService: NavigationService,
		private location: Location,
		private cdr: ChangeDetectorRef,
		private destroyRef: DestroyRef
	) {
		super(repository);
		this.validate();
		this.repository.venue.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(venue => {
			this.promoCategories = venue.articleCategories.filter(it => it.mainCategory === ArticleCategoryType.PROMOS);
		});
	}

	@Input() set enterToCheckout(value: boolean) {
		console.log('VALUE', value);
		if (value) this.openCheckoutModal();
	}

	get hasArticles(): boolean {
		return !!this.order && this.order.orderedArticles.length > 0;
	}

	get hasAbsoluteOrRelativePromo(): boolean {
		return !!(this.order && this.ou.isAbsoluteOrRelativePromo && this.ou.isAbsoluteOrRelativePromo(this.order));
	}

	get groupedArticles(): { articleGroup: ArticleGroup; originalIndices: number[] }[] {
		try {
			if (!this.order || !this.order.orderedArticles || this.order.orderedArticles.length === 0) {
				console.log('No articles to group');
				return [];
			}

			const grouped = new Map<string, { articleGroup: ArticleGroup; originalIndices: number[] }>();

			this.order.orderedArticles.forEach((articleGroup, index) => {
				try {
					if (!articleGroup || !articleGroup.article) {
						console.warn('Invalid article group at index', index);
						return;
					}

					const key = this.getArticleGroupKey(articleGroup);

					if (grouped.has(key)) {
						const existing = grouped.get(key);
						existing.articleGroup.quantity += articleGroup.quantity;
						existing.originalIndices.push(index);
					} else {
						const clonedGroup = JSON.parse(JSON.stringify(articleGroup)) as ArticleGroup;
						grouped.set(key, {
							articleGroup: clonedGroup,
							originalIndices: [index],
						});
					}
				} catch (e) {
					console.error('Error grouping article at index', index, e);
					// If there's an error, add it as a separate group
					try {
						const clonedGroup = JSON.parse(JSON.stringify(articleGroup)) as ArticleGroup;
						grouped.set(`error-${index}`, {
							articleGroup: clonedGroup,
							originalIndices: [index],
						});
					} catch (cloneError) {
						console.error('Could not clone article group:', cloneError);
					}
				}
			});

			const result = Array.from(grouped.values());
			return result;
		} catch (e) {
			console.error('Error in groupedArticles getter:', e);
			// Fallback: return ungrouped articles
			try {
				const fallback = (this.order?.orderedArticles || []).map((ag, index) => ({
					articleGroup: ag,
					originalIndices: [index],
				}));
				console.log('Using fallback grouping:', fallback.length);
				return fallback;
			} catch (fallbackError) {
				console.error('Fallback also failed:', fallbackError);
				return [];
			}
		}
	}

	ngOnInit() {
		super.ngOnInit();
		const recommendationSub = this.repository.recommendationList$.pipe(debounceTime(500)).subscribe(recommendationArt => {
			if (this.venue && this.venue.articleCategories && recommendationArt && recommendationArt.length > 0) {
				this.recommendationArticles = [];
				recommendationArt = recommendationArt.map(it => {
					return it;
				});
				const categoryArticles = [].concat(...this.venue.articleCategories.map(it => it.articles || [])) as Article[];
				const categoryArticlesMap = new Map(categoryArticles.map(it => [it._id, it]));
				recommendationArt.forEach(it => {
					if (it && it.articles && Array.isArray(it.articles)) {
						it.articles.forEach(art => {
							if (categoryArticlesMap.has(art)) {
								this.recommendationArticles.push(categoryArticlesMap.get(art));
							}
						});
					}
				});
				console.log(this.recommendationArticles);

				this.startAutoPlaySlider();
			} else {
				this.recommendationArticles = [];
			}
			this.cdr.markForCheck();
		});
		this.subscriptions.add(recommendationSub);
	}

	ngOnDestroy() {
		this.subscriptions.unsubscribe();
	}

	async startAutoPlaySlider() {
		try {
			await sleep(1500);
			this.swiper.nativeElement?.swiper?.update();
			this.swiper.nativeElement?.swiper?.autoplay?.start();
			// console.log(this.swiper.nativeElement.swiper.update())
		} catch (e) {
			console.log(e, 'Problem with playing swiper...');
		}
	}

	onVenue() {
		super.onVenue();
		this.validate();
		if (this.order) {
			this.calculateTotalPrice();
		}
		this.cdr.markForCheck();
	}

	onCustomer(): void {
		super.onCustomer();
		if (this.order) {
			this.calculateTotalPrice();
		}
		this.cdr.markForCheck();
	}

	onOrder() {
		super.onOrder();
		this.validate();
		if (this.order) {
			this.calculateTotalPrice();
		}
		this.cdr.markForCheck();
	}

	async onAction(index: number, event: OrderListAction) {
		switch (event) {
			case OrderListAction.add:
				this.order.orderedArticles[index].quantity += 1;
				break;
			case OrderListAction.delete:
				this.order.orderedArticles.splice(index, 1);
				break;
			case OrderListAction.note:
				await this.noteArticle(index);
				break;
			case OrderListAction.edit:
				const response = await ModalInfoComponent.show(this.modalCtrl, structuredClone(this.order.orderedArticles[index]), false);
				if (response) {
					this.order.orderedArticles[index] = response;
					this.repository.onOrderChange(this.order);
					this.cdr.markForCheck();
				}
				break;
			case OrderListAction.remove:
				if (this.order.orderedArticles[index].quantity > 1) {
					this.order.orderedArticles[index].quantity -= 1;
				} else {
					await this.onAction(index, OrderListAction.delete);
					return;
				}
				break;
		}
		this.repository.onOrderChange(this.order);
		this.cdr.markForCheck();
	}

	async openDiscountsModal() {
		// pulls in the barcode scanner (~430 kB), so it stays out of the initial bundle
		const { DiscountsModal } = await import('../discounts-modal/discounts-modal');
		const result: DiscountsModalResult = await DiscountsModal.show(this.modalCtrl);
		switch (result.discountType) {
			case DiscountType.StudentThursday:
				await this.repository.applyStudentsThursday();
				break;
			case DiscountType.Membership:
			case DiscountType.Partners:
			case DiscountType.Other: {
				if (!result.coupon?.length) {
					break;
				}
				const coupons = result.coupon.map(c => ({ ...c, mov: c.mov ? +c.mov : 0 }));
				const orderTotal = OrderUtils.orderTotalPrice(this.order, false, false);
				const belowMov = coupons.find(c => orderTotal < c.mov);
				if (belowMov) {
					this.snackbarCtrl.open(
						this.translate.instant('promo_code.mov_not_reached', {
							mov: numberToCurrency(belowMov.mov, this.order.currency),
						}),
						null,
						{ duration: 4000 }
					);
					break;
				}

				this.loading = true;
				this.cdr.markForCheck();
				try {
					let orderCopy = structuredClone(this.order);
					for (const coupon of coupons) {
						orderCopy = OrderUtils.applyPromo(this.translate, this.repository._venue, orderCopy, coupon);
					}
					orderCopy.userUid = this.repository.customer.uid;
					orderCopy.orderAt = null;
					await Api.applyPromoCode(orderCopy);

					this.order = orderCopy;

					this.repository.onOrderChange(this.order);

					this.calculateTotalPrice();
				} catch (e: any) {
					this.snackbarCtrl.open(axiosErrorToMessage(this.translate, e), null, { duration: 4000 });
				} finally {
					this.loading = false;
					this.cdr.markForCheck();
				}
				break;
			}
		}
	}

	async openCheckoutModal() {
		// Validate order
		this.validate();

		if (!this.isValid || this.loading) {
			return;
		}

		this.loading = true;
		this.cdr.detectChanges();

		// Set default values if missing
		if (!this.order.preorder.name && this.customer && this.customer.name) {
			this.order.preorder.name = this.customer.name;
		}
		if (!this.order.preorder.phone && this.customer && this.customer.phone) {
			this.order.preorder.phone = this.customer.phone;
		}

		if (!this.order.preorder.name) {
			this.order.preorder.name = 'defaultName';
		}

		if (!this.order.preorder.email) {
			this.order.preorder.email = 'topgolf@smoothr.de';
		}

		// Set order time to ASAP if not set
		if (!this.order.orderAt) {
			this.order.asap = true;
		}

		this.repository.onOrderChange(this.order);
		try {
			await this.repository.sendOrderWithoutNavToPayment(this.order, loading => {
				this.loading = loading;
			});
			await this.modalCtrl.dismiss();
			await this.navigationService.homeWithTable(this.venue.readableId.split('_')[1], this.order.tableNumber);
			const alert = await this.alertCtrl.create({
				header: this.translate.instant('order_content.order_accepted_title'),
				message: this.translate.instant('order_content.order_accepted_message'),
				buttons: ['OK'],
			});
			await alert.present();
			this.loading = false;
			this.cdr.markForCheck();
		} catch (e) {
			console.error('Error sending order:', e);
			this.snackbarCtrl.open(this.translate.instant('order_content.send_error'));
			this.loading = false;
			this.cdr.markForCheck();
			return;
		}
	}

	validate() {
		if (!this.order || !this.venue || !this.order.preorder) {
			this.isValid = false;
			this.cdr.markForCheck();
			return;
		}
		const result = OrderUtils.validateOrder(this.venue, this.order);

		this.differenceToMvo = result.movDifference;

		this.isValid = result.valid;
		this.cdr.markForCheck();
	}

	calculateTotalPrice() {
		if (!this.order) {
			this.totalPriceValue = '';
			this.totalPriceBeforeDiscount = '';
			this.promoDiscountValue = '';
			return;
		}

		try {
			// Check if backend has already applied promo discount (indicated by beforeDiscountPrice on articles)
			const backendHasAppliedPromo = this.order.orderedArticles.some(oa => oa.article && (oa.article as any).beforeDiscountPrice);

			let totalPrice = 0;
			let totalSumBeforeDiscount = 0;

			if (backendHasAppliedPromo) {
				// When backend has applied promo, use the totalPrice from orderedArticle (already discounted)
				totalPrice = this.order.orderedArticles.reduce((sum, oa) => {
					const price = numberD(oa.totalPrice);
					return sum + (price || 0);
				}, 0);

				// Sum of beforeDiscountTotalPrice values
				totalSumBeforeDiscount = this.order.orderedArticles.reduce((sum, oa) => {
					const price = numberD(oa.beforeDiscountTotalPrice);
					return sum + (price || 0);
				}, 0);
			} else {
				// Frontend applies promo normally
				totalPrice = OrderUtils.orderTotalPrice(this.order, true, true, null, true);
				totalSumBeforeDiscount = OrderUtils.orderTotalPrice(this.order, false, false);
			}

			this.totalPriceValue = numberToCurrency(totalPrice + this.tip, this.order.currency);
			this.totalPriceBeforeDiscount = numberToCurrency(totalSumBeforeDiscount >= 0 ? totalSumBeforeDiscount : 0, this.order.currency);

			// Calculate promo discount once and store it
			if (this.ou.isAbsoluteOrRelativePromo && this.ou.isAbsoluteOrRelativePromo(this.order)) {
				const discount = totalSumBeforeDiscount - totalPrice;
				this.promoDiscountValue = numberToCurrency(discount, this.order.currency);
			} else {
				this.promoDiscountValue = '';
			}

			try {
				this.amountOfPointsNotRegister = Math.round(totalPrice * 0.08);
			} catch (e) {}
		} catch (error) {
			console.error('Error calculating total price:', error);
			this.totalPriceValue = '';
			this.totalPriceBeforeDiscount = '';
			this.promoDiscountValue = '';
		}
	}

	replace(index: number, article: Article, options: ArticleOption[]) {
		this.order.orderedArticles[index].quantity = 1;
		this.order.orderedArticles[index].article = article;
		this.order.orderedArticles[index].groups = options;
	}

	onPromoApplied(order: Order) {
		this.promoError = null;
		this.repository.onOrderChange(order);
		this.cdr.markForCheck();
	}

	onDeletePromo(promoCode: PromoCode) {
		const order = OrderUtils.removePromo(this.order, promoCode);
		this.repository.onOrderChange(order);
		this.cdr.markForCheck();
	}

	async onRemovePromoCode() {
		if (!this.order || !this.order.promoCodes || this.order.promoCodes.length === 0) {
			return;
		}

		try {
			this.loading = true;
			this.cdr.markForCheck();

			// Get the first promo code to remove
			const promoCodeToRemove = this.order.promoCodes[0];

			// Use OrderUtils.removePromo to remove the promo code
			this.order = OrderUtils.removePromo(this.order, promoCodeToRemove);

			// Clear the promo code objects and reset article prices
			this.order.promoCodes = [];
			delete (this.order as any).promoCode;

			// Remove promo flags and discount-related properties from articles
			this.order.orderedArticles.forEach(oa => {
				oa.isPromo = false;
				oa.freeArticle = false;
				if (oa.article) {
					delete (oa.article as any).beforeDiscountPrice;
				}
				delete (oa as any).beforeDiscountTotalPrice;
				delete (oa as any).totalPrice;
			});

			// Update the repository with the cleared order
			this.repository.onOrderChange(this.order);
			this.calculateTotalPrice();
			this.cdr.markForCheck();
		} catch (e: any) {
			console.error('Error removing promo code:', e);
			this.snackbarCtrl.open(this.translate.instant('order_content.remove_promo_error'), null, { duration: 4000 });
		} finally {
			this.loading = false;
			this.cdr.markForCheck();
		}
	}

	async noteArticle(index: number) {
		const response = await NoteArticleModalComponent.show(
			this.modalCtrl,
			JSON.parse(JSON.stringify(this.order.orderedArticles[index]))
		);
		if (response?.data || response.data === '') {
			this.order.orderedArticles[index].note = response?.data;
			this.repository.onOrderChange(this.order);
			this.cdr.markForCheck();
		}
	}

	getTotalItemCount(): number {
		if (!this.order || !this.order.orderedArticles) {
			return 0;
		}
		return this.order.orderedArticles.reduce((total, ag) => total + ag.quantity, 0);
	}

	async onGroupedAction(originalIndices: number[], event: OrderListAction) {
		try {
			if (!originalIndices || originalIndices.length === 0) {
				console.error('No original indices provided');
				return;
			}

			// Handle action on grouped items
			switch (event) {
				case OrderListAction.add:
					// Add to the first item in the group
					await this.onAction(originalIndices[0], event);
					break;
				case OrderListAction.remove:
					// Remove from the last item in the group
					await this.onAction(originalIndices[originalIndices.length - 1], event);
					break;
				case OrderListAction.delete:
					// Delete all items in the group (in reverse order to maintain indices)
					for (let i = originalIndices.length - 1; i >= 0; i--) {
						await this.onAction(originalIndices[i], event);
					}
					break;
				case OrderListAction.note:
				case OrderListAction.edit:
					// For note and edit, only work on the first item
					await this.onAction(originalIndices[0], event);
					break;
			}
		} catch (e) {
			console.error('Error in onGroupedAction:', e);
		}
	}

	addArticle(article: Article) {}

	close() {
		this.closeOrderContent.emit();
	}

	async openModal(item: Article) {
		if (this.loading) {
			return;
		}

		const articleGroup = new ArticleGroup();
		articleGroup.article = item;
		articleGroup.groups.push(...defaultsToArticleOption(item, [], item.defaults, this.order?.type, this.order.preorder.type));
		articleGroup.quantity = 1;

		const response = await ModalInfoComponent.show(this.modalCtrl, articleGroup, true, 'info-product-modal', 'ios');
		if (response) {
			OrderUtils.addToOrder(this.order, response);

			this.repository.onOrderChange(this.order);
			this.cdr.markForCheck();
		}
	}

	private getArticleGroupKey(articleGroup: ArticleGroup): string {
		try {
			// Create a unique key based on article ID, options, and note
			const optionsKey = (articleGroup.groups || [])
				.map(opt => `${opt.article?._id || ''}-${opt.quantity || 0}-${opt.group || ''}`)
				.sort()
				.join('|');

			return `${articleGroup.article?._id || 'unknown'}-${optionsKey}-${articleGroup.note || ''}-${articleGroup.isPromo || false}`;
		} catch (e) {
			console.error('Error generating article group key:', e);
			// Fallback to a simple key if there's an error
			return `${articleGroup.article?._id || 'unknown'}-${Math.random()}`;
		}
	}

	protected readonly environment = environment;
}
